/**
 * API Client
 * HTTP client with automatic token handling and error management
 *
 * Token Strategy:
 * - Uses Firebase getIdToken() which ALWAYS returns a valid token
 * - If token is expired, Firebase SDK automatically refreshes it
 * - No need for complex 401 retry logic - token is always fresh
 */

import { API_URL, TOKEN_KEYS, REQUEST_TIMEOUT, HTTP_STATUS } from '@/config/api.config';

// API Error class
export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

// Token management
export const tokenManager = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEYS.ACCESS);
  },
  
  getRefreshToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEYS.REFRESH);
  },
  
  setTokens: (access: string, refresh?: string) => {
    localStorage.setItem(TOKEN_KEYS.ACCESS, access);
    if (refresh) {
      localStorage.setItem(TOKEN_KEYS.REFRESH, refresh);
    }
  },
  
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEYS.ACCESS);
    localStorage.removeItem(TOKEN_KEYS.REFRESH);
    localStorage.removeItem(TOKEN_KEYS.USER);
    localStorage.removeItem(TOKEN_KEYS.FIREBASE);
  },
  
  getUser: () => {
    const userStr = localStorage.getItem(TOKEN_KEYS.USER);
    return userStr ? JSON.parse(userStr) : null;
  },
  
  setUser: (user: any) => {
    localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(user));
  },
};

// Request configuration
interface RequestConfig extends RequestInit {
  timeout?: number;
  skipAuth?: boolean;
  params?: Record<string, any>;  // ✅ NEW: Query parameters support
  responseType?: 'json' | 'text' | 'blob';  // ✅ NEW: Response type support for file downloads
}

// API Client
class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  // Mutex to prevent concurrent token refresh calls
  private tokenRefreshPromise: Promise<string | null> | null = null;

  /**
   * Get a fresh Firebase token
   * This method ALWAYS returns a valid token because Firebase SDK
   * automatically refreshes expired tokens internally
   *
   * Uses a mutex pattern to prevent multiple concurrent calls to getIdToken()
   * when multiple requests are made simultaneously.
   *
   * @returns Fresh token or null if not authenticated
   */
  /**
   * Token público para callers que necesitan hacer ``fetch`` directo
   * (ej. respuestas streaming que el método ``request`` no expone).
   * Reutiliza la misma resolución de token de Firebase + fallbacks.
   */
  async getValidToken(): Promise<string | null> {
    return this.getFreshFirebaseToken();
  }

  private async getFreshFirebaseToken(): Promise<string | null> {
    // If a token refresh is already in progress, wait for it
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.tokenRefreshPromise = this.doGetFreshFirebaseToken();

    try {
      return await this.tokenRefreshPromise;
    } finally {
      this.tokenRefreshPromise = null;
    }
  }

  /**
   * Internal method that actually fetches the token
   */
  private async doGetFreshFirebaseToken(): Promise<string | null> {
    // Don't interact with Firebase when running inside an iframe
    // (e.g., heatmap preview) - public routes don't need auth
    if (window.self !== window.top) {
      return null;
    }

    try {
      // Dynamically import to avoid circular dependency
      const { auth } = await import('@/services/firebase/firebaseConfig');

      if (auth?.currentUser) {
        // getIdToken() ALWAYS returns a valid token
        // If the cached token is expired, Firebase SDK refreshes it automatically
        // This is the key to avoiding 401 errors!
        const token = await auth.currentUser.getIdToken();

        // Update localStorage cache for other parts of the app
        localStorage.setItem(TOKEN_KEYS.FIREBASE, token);

        return token;
      }

      // No Firebase user - try localStorage as fallback
      return localStorage.getItem(TOKEN_KEYS.FIREBASE);
    } catch (error) {
      console.error('❌ [apiClient] Error getting fresh Firebase token:', error);
      // Fallback to cached token
      return localStorage.getItem(TOKEN_KEYS.FIREBASE);
    }
  }

  /**
   * Convert params object to query string
   */
  private buildQueryString(params?: Record<string, any>): string {
    if (!params) return '';

    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Create base request headers (WITHOUT authentication)
   * Authentication is handled separately in request() using getFreshFirebaseToken()
   * @param skipAuth - Not used anymore, kept for backward compatibility
   * @param isFormData - If true, don't set Content-Type (browser sets it with boundary for FormData)
   */
  private getHeaders(_skipAuth: boolean = false, isFormData: boolean = false): Record<string, string> {
    const headers: Record<string, string> = {
      'Accept': 'application/json',
    };

    // Don't set Content-Type for FormData - browser sets it automatically with boundary
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    // NOTE: Authorization header is now added in request() method
    // using getFreshFirebaseToken() to ensure token is always valid

    return headers;
  }

  /**
   * Handle response errors
   */
  private async handleResponse<T>(response: Response, responseType?: 'json' | 'text' | 'blob'): Promise<T> {
    if (response.ok) {
      // Handle 204 No Content
      if (response.status === HTTP_STATUS.NO_CONTENT) {
        return {} as T;
      }

      // Handle blob response type (for file downloads)
      if (responseType === 'blob') {
        return await response.blob() as T;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return await response.text() as T;
    }

    // Handle errors
    let errorData: any = {};
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: response.statusText };
    }

    // Only log unexpected errors (not 404 or expected business errors)
    const errorString = typeof errorData.error === 'string' ? errorData.error : '';
    const isExpectedError =
      response.status === 404 ||
      errorString.includes('No active pipeline') ||
      errorString.includes('not found') ||
      errorString.includes('not associated with a company') ||
      errorString.includes('No company associated');

    if (!isExpectedError) {
      console.error('❌ [apiClient] Error response:', errorData);
    }

    const errorMessage = errorData.detail || errorData.message || errorData.error || 'An error occurred';
    throw new APIError(errorMessage, response.status, errorData);
  }

  /**
   * Make HTTP request with fresh token
   * Token is obtained fresh before each request via getFreshFirebaseToken()
   * which guarantees the token is always valid (Firebase SDK auto-refreshes)
   */
  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const { timeout = REQUEST_TIMEOUT, skipAuth = false, params, responseType, ...fetchConfig } = config;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Check if body is FormData
    const isFormData = fetchConfig.body instanceof FormData;

    try {
      // Build URL with query parameters
      const queryString = this.buildQueryString(params);
      const url = `${this.baseURL}${endpoint}${queryString}`;

      // Get headers (without auth)
      const headers = this.getHeaders(skipAuth, isFormData);

      // Get FRESH token before request (this is the key improvement!)
      // Skip auth entirely in iframes - public routes don't need auth tokens
      if (!skipAuth && window.self === window.top) {
        const freshToken = await this.getFreshFirebaseToken();
        if (freshToken) {
          headers['Authorization'] = `Bearer ${freshToken}`;
        } else {
          // No Firebase token - try portal token first, then JWT fallback
          const portalToken = localStorage.getItem('portal_token');
          if (portalToken) {
            headers['Authorization'] = `Bearer ${portalToken}`;
          } else {
            const jwtToken = tokenManager.getAccessToken();
            if (jwtToken) {
              headers['Authorization'] = `Bearer ${jwtToken}`;
            }
          }
        }
      }

      const response = await fetch(url, {
        ...fetchConfig,
        headers: {
          ...headers,
          ...fetchConfig.headers,
        },
        signal: controller.signal,
      });

      // Handle 401 Unauthorized
      // This should be rare now since we get fresh token before each request
      // But we keep it as a safety net for edge cases (e.g., token revoked server-side)
      if (response.status === HTTP_STATUS.UNAUTHORIZED && !skipAuth && !endpoint.includes('/auth/')) {
        console.warn('⚠️ [apiClient] Got 401 despite fresh token - session may be revoked');

        // Don't clear tokens or redirect if running inside an iframe (e.g., heatmap preview)
        // Clearing localStorage in an iframe would log out the parent window too
        if (window.self === window.top) {
          // Clear all tokens (includes FIREBASE token)
          tokenManager.clearTokens();

          // Redirect to login - use return to avoid throwing after redirect
          // This prevents React from showing error UI during navigation
          window.location.href = '/login';
          return new Promise(() => {}); // Never resolves - page is navigating away
        }
      }

      // Handle 403 with token_not_valid - this happens when Firebase token verification
      // fails temporarily (network blip, clock skew). Retry once with a force-refreshed token.
      if (response.status === HTTP_STATUS.FORBIDDEN && !skipAuth && !endpoint.includes('/auth/')) {
        try {
          const errorBody = await response.clone().json();
          const errorStr = JSON.stringify(errorBody);
          const isTokenError = errorStr.includes('token_not_valid') ||
            errorStr.includes('Firebase token invalid') ||
            errorStr.includes('Unable to verify Firebase token');

          if (isTokenError && window.self === window.top) {
            console.warn('⚠️ [apiClient] Got 403 token error - retrying with force-refreshed token');

            // Force refresh Firebase token
            try {
              const { auth } = await import('@/services/firebase/firebaseConfig');
              if (auth?.currentUser) {
                const freshToken = await auth.currentUser.getIdToken(true); // force refresh
                localStorage.setItem(TOKEN_KEYS.FIREBASE, freshToken);

                // Retry the request once with the fresh token
                const retryHeaders = { ...headers, ...fetchConfig.headers, 'Authorization': `Bearer ${freshToken}` };
                const retryResponse = await fetch(url, {
                  ...fetchConfig,
                  headers: retryHeaders,
                  signal: AbortSignal.timeout(timeout),
                });

                if (retryResponse.ok || (retryResponse.status !== HTTP_STATUS.UNAUTHORIZED && retryResponse.status !== HTTP_STATUS.FORBIDDEN)) {
                  return this.handleResponse<T>(retryResponse, responseType);
                }

                // Retry also failed with auth error - redirect to login
                console.error('❌ [apiClient] Retry with fresh token also failed - redirecting to login');
                tokenManager.clearTokens();
                window.location.href = '/login';
                return new Promise(() => {});
              }
            } catch (retryError) {
              console.error('❌ [apiClient] Failed to retry with fresh token:', retryError);
            }
          }
        } catch {
          // Could not parse error body - continue with normal error handling
        }
      }

      return this.handleResponse<T>(response, responseType);
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new APIError('Request timeout', 408);
        }
        // Don't log expected errors
        if (!error.message.includes('Session expired')) {
          console.error('❌ [apiClient] Request error:', error);
        }
        throw new APIError(error.message, 0);
      }

      throw new APIError('Unknown error occurred', 0);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    // Don't stringify FormData - pass it directly
    const body = data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined);
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    
    const result = await this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
    
    
    return result;
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'DELETE',
    });
  }

  /**
   * Upload file(s) with FormData (multipart/form-data)
   * This method handles file uploads without setting Content-Type header
   * (browser will set it automatically with boundary)
   */
  async upload<T>(endpoint: string, formData: FormData, config?: Omit<RequestConfig, 'body'>): Promise<T> {
    const { timeout = REQUEST_TIMEOUT, skipAuth = false, params, ...fetchConfig } = config || {};

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Build URL with query parameters
      const queryString = this.buildQueryString(params);
      const url = `${this.baseURL}${endpoint}${queryString}`;

      // Get headers but remove Content-Type (let browser set it for FormData)
      const headers: HeadersInit = {
        'Accept': 'application/json',
      };

      // Get FRESH token before upload
      // Skip auth entirely in iframes - public routes don't need auth tokens
      if (!skipAuth && window.self === window.top) {
        const freshToken = await this.getFreshFirebaseToken();
        if (freshToken) {
          headers['Authorization'] = `Bearer ${freshToken}`;
        } else {
          // No Firebase token - try portal token first, then JWT fallback
          const portalToken = localStorage.getItem('portal_token');
          if (portalToken) {
            headers['Authorization'] = `Bearer ${portalToken}`;
          } else {
            const token = tokenManager.getAccessToken();
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }
          }
        }
      }

      const response = await fetch(url, {
        ...fetchConfig,
        method: 'POST',
        headers: {
          ...headers,
          ...fetchConfig.headers,
        },
        body: formData,
        signal: controller.signal,
      });

      // Handle 401 Unauthorized (rare - token should always be fresh)
      if (response.status === HTTP_STATUS.UNAUTHORIZED && !skipAuth && !endpoint.includes('/auth/')) {
        console.warn('⚠️ [apiClient] Got 401 on upload despite fresh token - session may be revoked');

        // Don't clear tokens or redirect if running inside an iframe
        if (window.self === window.top) {
          // Clear all tokens (includes FIREBASE token)
          tokenManager.clearTokens();

          // Redirect to login - use return to avoid throwing after redirect
          window.location.href = '/login';
          return new Promise(() => {}); // Never resolves - page is navigating away
        }
      }

      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new APIError('Request timeout', 408);
        }
        if (!error.message.includes('Session expired')) {
          console.error('❌ [apiClient] Upload error:', error);
        }
        throw new APIError(error.message, 0);
      }

      throw new APIError('Unknown error occurred', 0);
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient(API_URL);

// Export for testing
export { ApiClient };

